import { useEffect, useMemo, useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { Modale } from '../ui/Modale';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  MessageErreur,
  Pastille,
  PastilleSite,
} from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import { usePlanifierEcf, type PromotionEcf } from '../../lib/useEcf';
import { jourLisible } from './format';

/**
 * Planifier une ECF : un bloc du programme, une date, les apprenants d'une
 * promotion. Les journées ECF du calendrier d'alternance de la formation sont
 * proposées — le calendrier les tient déjà, on ne les ressaisit pas. Ce qui
 * empêche un apprenant d'être planifié (bloc acquis, ECF déjà en cours) vient
 * de l'API, ligne par ligne.
 */

const CLASSE_CHAMP =
  'min-h-[44px] w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-codialis-navy';
const CLASSE_LIBELLE = 'mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy';

export function ModalePlanificationEcf({
  ouverte,
  onFermer,
  promotions,
}: {
  ouverte: boolean;
  onFermer: () => void;
  promotions: PromotionEcf[];
}): JSX.Element | null {
  const notifier = useToast();
  const planifier = usePlanifierEcf();
  const planifiables = useMemo(
    () => promotions.filter((promotion) => promotion.auProgramme),
    [promotions],
  );
  const sansEcf = promotions.filter((promotion) => !promotion.auProgramme);

  const [promotionCle, setPromotionCle] = useState('');
  const [blocId, setBlocId] = useState('');
  const [date, setDate] = useState('');
  const [choisis, setChoisis] = useState<Set<string>>(new Set());

  const promotion = planifiables.find((candidate) => candidate.cle === promotionCle);

  useEffect(() => {
    if (!ouverte) return;
    setPromotionCle(planifiables[0]?.cle ?? '');
    setDate('');
    planifier.reset();
    // Réinitialisée à chaque ouverture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouverte]);

  // Changer de promotion propose le premier bloc où quelqu'un reste à
  // planifier, à défaut le premier du programme.
  useEffect(() => {
    const ouvert = promotion?.blocs.find((bloc) =>
      promotion.apprenants.some((apprenant) => apprenant.blocs[bloc.id]?.refus === null),
    );
    setBlocId(ouvert?.id ?? promotion?.blocs[0]?.id ?? '');
  }, [promotion?.cle]); // eslint-disable-line react-hooks/exhaustive-deps

  // Les apprenants planifiables sont cochés d'office, à chaque bloc choisi.
  useEffect(() => {
    if (!promotion || !blocId) {
      setChoisis(new Set());
      return;
    }
    setChoisis(
      new Set(
        promotion.apprenants
          .filter((apprenant) => apprenant.blocs[blocId]?.refus === null)
          .map((apprenant) => apprenant.id),
      ),
    );
    // Relu à chaque promotion ou bloc choisi, pas à chaque rafraîchissement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promotion?.cle, blocId]);

  function basculer(id: string): void {
    setChoisis((avant) => {
      const suivant = new Set(avant);
      if (suivant.has(id)) suivant.delete(id);
      else suivant.add(id);
      return suivant;
    });
  }

  function valider(): void {
    planifier.mutate(
      { blocId, datePrevue: date, apprenantIds: [...choisis] },
      {
        onSuccess: ({ message }) => {
          notifier(message);
          onFermer();
        },
      },
    );
  }

  return (
    <Modale
      titre="Planifier une ECF"
      sousTitre="Un bloc du programme, une date, les apprenants de la promotion"
      ouverte={ouverte}
      onFermer={onFermer}
    >
      <div className="space-y-4 p-4 sm:p-6">
        {planifiables.length === 0 ? (
          <p className="text-sm text-codialis-gray">
            Aucune promotion suivie n’attend d’ECF : le programme de leur formation ne prévoit pas
            d’heures d’ECF, ou n’est pas découpé en blocs.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block min-w-0">
                <span className={CLASSE_LIBELLE}>Promotion</span>
                <select
                  value={promotionCle}
                  onChange={(evenement) => setPromotionCle(evenement.target.value)}
                  className={CLASSE_CHAMP}
                >
                  {planifiables.map((candidate) => (
                    <option key={candidate.cle} value={candidate.cle}>
                      {candidate.libelle} · {candidate.siteCode}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block min-w-0">
                <span className={CLASSE_LIBELLE}>Bloc évalué</span>
                <select
                  value={blocId}
                  onChange={(evenement) => setBlocId(evenement.target.value)}
                  className={CLASSE_CHAMP}
                >
                  {promotion?.blocs.map((bloc) => (
                    <option key={bloc.id} value={bloc.id}>
                      {bloc.libelle}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div>
              <label className="block max-w-xs">
                <span className={CLASSE_LIBELLE}>Date de l’ECF</span>
                <input
                  type="date"
                  value={date}
                  onChange={(evenement) => setDate(evenement.target.value)}
                  className={CLASSE_CHAMP}
                />
              </label>
              {promotion && promotion.joursEcf.length > 0 && (
                <div className="mt-2">
                  <div className="mb-1.5 flex items-center gap-1.5 text-[11px] text-codialis-gray">
                    <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
                    Journées ECF du calendrier d’alternance
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {promotion.joursEcf.map((jour) => (
                      <button
                        key={jour}
                        type="button"
                        onClick={() => setDate(jour)}
                        aria-pressed={date === jour}
                        className={`min-h-[36px] rounded-lg px-3 text-xs font-semibold transition [@media(pointer:coarse)]:min-h-[40px] ${
                          date === jour
                            ? 'bg-codialis-navy text-white'
                            : 'border border-gray-200 bg-white text-codialis-navy hover:bg-gray-50'
                        }`}
                      >
                        {jourLisible(jour)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {promotion && (
              <fieldset>
                <legend className={CLASSE_LIBELLE}>
                  Apprenants ({choisis.size}/{promotion.apprenants.length})
                </legend>
                <ul className="divide-y divide-gray-100 rounded-xl border border-gray-100">
                  {promotion.apprenants.map((apprenant) => {
                    const etat = apprenant.blocs[blocId];
                    const refus = etat?.refus ?? null;
                    return (
                      <li key={apprenant.id}>
                        <label
                          className={`flex min-h-[44px] items-start gap-3 px-3 py-2.5 ${
                            refus ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={choisis.has(apprenant.id)}
                            disabled={refus !== null}
                            onChange={() => basculer(apprenant.id)}
                            className="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-codialis-navy">
                              {apprenant.nom}
                            </span>
                            {refus && (
                              <span className="block text-[11px] text-codialis-gray">{refus}</span>
                            )}
                          </span>
                          {etat && <Pastille texte={etat.etatLibelle} ton={etat.etatTon} />}
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </fieldset>
            )}
          </>
        )}

        {sansEcf.length > 0 && (
          <div className="rounded-xl bg-codialis-sand p-3 text-xs text-codialis-gray">
            <div className="mb-1 font-semibold text-codialis-navy">Promotions sans ECF au programme</div>
            <ul className="space-y-1">
              {sansEcf.map((candidate) => (
                <li key={candidate.cle} className="flex flex-wrap items-center gap-1.5">
                  <PastilleSite site={candidate.siteCode} />
                  <span>
                    {candidate.libelle} — {candidate.motif}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {planifier.error instanceof ErreurApi && <MessageErreur message={planifier.error.message} />}

        <div className="flex flex-wrap justify-end gap-2">
          <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
          <BoutonPrincipal
            onClick={valider}
            disabled={!blocId || !date || choisis.size === 0 || planifier.isPending}
          >
            {planifier.isPending ? 'Planification…' : 'Planifier'}
          </BoutonPrincipal>
        </div>
      </div>
    </Modale>
  );
}
